import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import type { QrDimensions, QrDimensionFunction } from 'html5-qrcode/core';

const QRScanner = ({
  onScanSuccess,
  onScanFailure,
}: {
  onScanSuccess: (decodedText: string) => void;
  onScanFailure: (error: string) => void;
}) => {
  const qrCodeRegionId = 'qr-code-full-region';
  let html5QrCode: Html5Qrcode | null = null;

  const startScanner = async () => {
    try {
      if (!html5QrCode) {
        html5QrCode = new Html5Qrcode(qrCodeRegionId, false);
      }

      if (
        html5QrCode.isScanning ||
        html5QrCode.getState() === Html5QrcodeScannerState.SCANNING
      ) {
        return;
      }
      const qrboxFunction: QrDimensionFunction = (viewfinderWidth, viewfinderHeight) => {
        const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
        const qrboxSize = Math.floor(minEdge * 0.9);
        return {
          width: qrboxSize,
          height: qrboxSize,
        };
      };
      const config = {
        fps: 2,
        qrbox: qrboxFunction,
        aspectRatio: 1.0,
      };

      await html5QrCode.start(
        { facingMode: 'environment' },
        config,
        onScanSuccess,
        onScanFailure
      );
    } catch (err) {
      console.error('Error starting scanner:', err);
      onScanFailure('Error al iniciar el escaner');
    }
  };

  const stopScanner = async () => {
    if (html5QrCode && html5QrCode.isScanning) {
      try {
        await html5QrCode.stop();
        console.log('QR Code scanning stopped.');
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
  };

  React.useEffect(() => {
    startScanner();
    return () => {
      stopScanner();
    };
  }, []);

  return <div id={qrCodeRegionId} style={{ width: '100%', height: '100%' }} />;
};


const SessionExpired = () => {
  const navigate = useNavigate();
  const [isScanning, setIsScanning] = React.useState(false);
  const [scanError, setScanError] = React.useState<string | null>(null);

  const handleScanSuccess = (decodedText: string) => {
    try {
      // Assuming decoded text is a URL like /table/some-uuid
      const url = new URL(decodedText);
      const pathSegments = url.pathname.split('/').filter(Boolean); // filter out empty strings
  
      if (pathSegments.length >= 2 && pathSegments[0] === 'table') {
        const tableId = pathSegments[1];
        localStorage.setItem('tableId', tableId);
        navigate(`/menu/${tableId}`);
      } else {
        setScanError('Código QR no válido');
      }
    } catch(err) {
      setScanError('Error al procesar el código QR');
    }
  };

  const handleScanFailure = (error: string) => {
    console.error(`QR scan error: ${error}`);
    setScanError('No se pudo escanear el código QR. Por favor, inténtelo de nuevo.');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      width: '100vw',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      paddingLeft: 'env(safe-area-inset-left, 0px)',
      paddingRight: 'env(safe-area-inset-right, 0px)',
    }}>
      <Container maxWidth="sm" sx={{ px: 2 }}>
        <Card sx={{
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          position: 'relative'
        }}>
           {isScanning && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              background: 'black',
            }}>
              <QRScanner
                onScanSuccess={handleScanSuccess}
                onScanFailure={handleScanFailure}
              />
              <Button onClick={() => setIsScanning(false)} sx={{position: 'absolute', top: 16, right: 16, color: 'white'}}>
                Cerrar
              </Button>
            </Box>
          )}

          <CardContent sx={{
            textAlign: 'center',
            p: 4,
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}>
              <ErrorOutlineIcon sx={{
                fontSize: 80,
                color: '#ff6b6b',
              }} />
            </Box>

            <Typography variant="h4" component="h1" sx={{
              fontWeight: 700,
              color: '#2c3e50',
              mb: 2,
              fontSize: { xs: '1.75rem', sm: '2rem' }
            }}>
              Sesión Expirada
            </Typography>

            <Typography variant="body1" sx={{
              color: '#7f8c8d',
              mb: 1,
              lineHeight: 1.6,
              fontSize: '1.1rem'
            }}>
              Tu sesión ha expirado y ya no puedes agregar más artículos.
            </Typography>

            <Typography variant="body1" sx={{
              color: '#7f8c8d',
              mb: 4,
              lineHeight: 1.6,
              fontSize: '1.1rem'
            }}>
              Para continuar ordenando, debes escanear el código QR de la mesa nuevamente.
            </Typography>

            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}>
              <QrCodeScannerIcon sx={{
                fontSize: 60,
                color: '#3498db',
                cursor: 'pointer'
              }} 
              onClick={() => setIsScanning(true)}
              />
            </Box>           

            <Typography variant="caption" sx={{
              display: 'block',
              color: '#95a5a6',
              mt: 3,
              fontSize: '0.9rem'
            }}>
              Apunta la cámara de tu teléfono al código QR en tu mesa
            </Typography>

            {scanError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {scanError}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default SessionExpired; 